import {
  applyFolderGrantsToFile,
  effectiveRole,
  rederiveSharedWith,
} from './driveShares';

function trimTrailingSlashes(path) {
  if (!path) return '';
  const trimmed = String(path).replace(/\/+$/, '');
  return trimmed || '/';
}

function normalizeAllowedExtensions(allowedExtensions) {
  return new Set(
    (allowedExtensions || []).map((extension) =>
      String(extension).replace(/^\./, '').toLowerCase()
    )
  );
}

function joinPath(folderPath, name) {
  const normalizedFolder = trimTrailingSlashes(folderPath);
  return normalizedFolder === '/' ? `/${name}` : `${normalizedFolder}/${name}`;
}

export function isDirectChildPath(parentPath, childPath) {
  const normalizedParent = trimTrailingSlashes(parentPath);
  const normalizedChild = trimTrailingSlashes(childPath);

  if (!normalizedParent || !normalizedChild || normalizedParent === normalizedChild) {
    return false;
  }

  const prefix = normalizedParent === '/' ? '/' : `${normalizedParent}/`;
  if (!normalizedChild.startsWith(prefix)) {
    return false;
  }

  const remainder = normalizedChild.slice(prefix.length);
  return Boolean(remainder) && !remainder.includes('/');
}

export function extensionOfPath(path) {
  const basename = String(path || '').split('/').pop() || '';
  const dotIndex = basename.lastIndexOf('.');
  if (dotIndex <= 0 || dotIndex === basename.length - 1) {
    return '';
  }
  return basename.slice(dotIndex + 1).toLowerCase();
}

export function isVisibleAllowedStorageFile(item, allowedExtensions) {
  if (item?.id === null || item?.id === undefined) {
    return false;
  }

  const name = item?.name || '';
  if (!name || name.startsWith('.')) {
    return false;
  }

  return normalizeAllowedExtensions(allowedExtensions).has(extensionOfPath(name));
}

export function listDirectStorageFilePaths(folderPath, storageItems, allowedExtensions) {
  return (storageItems || [])
    .filter((item) => isVisibleAllowedStorageFile(item, allowedExtensions))
    .map((item) => joinPath(folderPath, item.name))
    .sort();
}

export function mergeDirectChildFileRows({
  folderPath,
  storageItems,
  documentRows,
  allowedExtensions,
}) {
  const rowsByPath = new Map();

  for (const documentRow of documentRows || []) {
    const path = documentRow?.path;
    if (
      !path ||
      documentRow?.is_folder === true ||
      !isDirectChildPath(folderPath, path)
    ) {
      continue;
    }

    rowsByPath.set(path, {
      path,
      documentRow,
      storageItem: null,
    });
  }

  for (const storageItem of storageItems || []) {
    if (!isVisibleAllowedStorageFile(storageItem, allowedExtensions)) {
      continue;
    }

    const path = joinPath(folderPath, storageItem.name);
    const existing = rowsByPath.get(path);
    rowsByPath.set(path, {
      path,
      documentRow: existing?.documentRow || null,
      storageItem,
    });
  }

  return Array.from(rowsByPath.values()).sort((a, b) => a.path.localeCompare(b.path));
}

export function buildInheritedFileMetadata({
  path,
  existingRow = null,
  folderPath,
  folderGrants,
  nowIso,
}) {
  const baseGrants = (existingRow?.share_grants || []).filter(
    (grant) => grant.source !== 'folder' || grant.folder_path !== folderPath
  );
  const shareGrants = applyFolderGrantsToFile(
    baseGrants,
    folderGrants,
    folderPath
  );

  return {
    path,
    content: existingRow?.content ?? {},
    latest_update: existingRow?.latest_update ?? nowIso,
    client_id: existingRow?.client_id ?? 'drive',
    is_folder: false,
    share_grants: shareGrants,
    shared_with: rederiveSharedWith(shareGrants),
  };
}

export function driveCapabilities({ isOwner, effectiveRole: role }) {
  const isAdmin = isOwner || role === 'admin';
  const canEdit = isAdmin || role === 'editor';
  const canRead = isAdmin || canEdit || role === 'read';

  return {
    canRead,
    canEdit,
    canUploadHere: canEdit,
    canShare: isAdmin,
    canDelete: isAdmin,
    canGenerateLink: isAdmin,
    canRename: isAdmin,
  };
}

export function effectiveDriveRole(grants, userId) {
  return effectiveRole(grants || [], userId);
}
